import { api } from '@/modules/api/index'
import { isEmpty, path, pathOr } from 'ramda'
import * as types from '../mutation-types'
import defaultStyleSettings from '@/utils/defaultGlobalStyleSettings'

const state = {
  files: [],
  settings: null,
  forbiddenEmailDomains: [],
  domainsTotalPages: '',
  domainsTotalItems: ''
}
const getters = {
  getFileById: state => id => state.files.find(file => file.id === id),
  getSettings: state => state.settings,
  isGlobalStyleSettings: state => !!path(['settings', 'globalStyleSettings'], state),
  venueSettings: path(['settings', 'venuePageSettings']),
  agendaSetting: path(['settings', 'agendaSetting']),
  getEmailTemplatesSettings: path(['settings', 'emailTemplatesSetting']),
  emailTemplateLayout: path(['settings', 'emailTemplatesSetting', 'emailTemplateLayout']),
  getBlockAttendeeSettings: pathOr({'allowedNumberOfAttemptsPerTime': 0, 'loginTimeInMinutes': 0}, ['settings', 'globalSetting', 'blockAttendeeSettings']),
  getAwardsSettings: path(['settings', 'awardsSetting']),
  getVideosDisplaySettings: path(['settings', 'videoGallerySetting', 'metadataObject']),
  getSponsorsSettings: path(['settings', 'sponsorsSetting', 'displaySettingsObject']),
  getGoogleSheetsSettings: path(['settings', 'googleSheetsSettings', 'messageTypeSettingsMap']),
  getVatIdSettings: path(['settings', 'globalSetting', 'vatIdSettings']),
  getVatIdValidationEnabled: path(['settings', 'globalSetting', 'vatIdValidationEnabled']),
  getGooglePixelSetting: path(['settings', 'googlePixelSetting']),
  getForbiddenEmailDomains: state => state.forbiddenEmailDomains,
  getDomainsTotalPages: state => state.domainsTotalPages,
  getDomainsTotalItems: state => state.domainsTotalItems,
  eventLink: state => pathOr('https://ifxexpo.com/cyprus2020', ['settings', 'globalSetting', 'eventUrl'], state),
  getBlitzSettingHideEnvelope: path(['settings', 'blitzSetting', 'hideEnvelope'])
}

export const getEventLink = () => getters.eventLink(state)

const actions = {
  async updateGeneralSettings ({ dispatch, commit }, payload) {
    try {
      commit(types.SET_SPINNER_STATUS, true)
      await this.$axios.put(api.generalSettings.updateSetting(), payload)
      commit(types.CREATE_SUCCESS_MESSAGE, 'General settings was updated successfully')
    } catch (err) {
      commit(types.CREATE_ERROR_MESSAGE, 'General settings wasn\'t updated')
      throw err
    } finally {
      commit(types.SET_SPINNER_STATUS, false)
    }
  },
  async fetchGeneralSettings ({commit}) {
    try {
      commit(types.SET_SPINNER_STATUS, true)
      const { data } = await this.$axios.get(api.generalSettings.getSettings())
      commit(types.SET_SETTINGS, data)
    } catch (err) {
    } finally {
      commit(types.SET_SPINNER_STATUS, false)
    }
  },
  async updateVatIdSettings ({ commit }, payload) {
    try {
      commit(types.SET_SPINNER_STATUS, true)
      await this.$axios.put(api.generalSettings.updateVatIdSettings(), payload)
      commit(types.CREATE_SUCCESS_MESSAGE, 'VatId settings was updated successfully')
    } catch (err) {
      commit(types.CREATE_ERROR_MESSAGE, 'VatId settings wasn\'t updated')
    } finally {
      commit(types.SET_SPINNER_STATUS, false)
    }
  },
  async fetchForbiddenDomains ({ commit }, payload = {}) {
    try {
      commit(types.SET_SPINNER_STATUS, true)
      const { data } = await this.$axios.get(api.generalSettings.getDomains(payload))
      commit(types.SET_FORBIDDEN_EMAIL_DOMAINS, data)
    } catch (err) {
      commit(types.CREATE_ERROR_MESSAGE, 'Failed to fetch forbidden email domains')
      throw err
    } finally {
      commit(types.SET_SPINNER_STATUS, false)
    }
  },
  async saveForbiddenDomain ({ commit }, payload) {
    try {
      commit(types.SET_SPINNER_STATUS, true)
      await this.$axios.post(api.generalSettings.postDomain(), payload)
      commit(types.CREATE_SUCCESS_MESSAGE, 'Domain has been saved')
    } catch (err) {
      if (path(['response', 'data', 'Code'], err) === 'validation.base-0001') {
        commit(types.CREATE_ERROR_MESSAGE, path(['response', 'data', 'ValidationFailures', [0], 'errorMessage'], err))
      } else {
        commit(types.CREATE_ERROR_MESSAGE, 'Failed to save forbidden email domain')
      }
      throw err
    } finally {
      commit(types.SET_SPINNER_STATUS, false)
    }
  },
  async saveForbiddenEmailPrefix ({ commit }, payload) {
    try {
      payload.description = payload.prefixDescription
      delete payload.domain
      delete payload.prefixDescription
      commit(types.SET_SPINNER_STATUS, true)
      await this.$axios.post(api.generalSettings.postPrefix(), payload)
      commit(types.CREATE_SUCCESS_MESSAGE, 'Prefix has been saved')
    } catch (err) {
      if (path(['response', 'data', 'Code'], err) === 'validation.base-0001') {
        commit(types.CREATE_ERROR_MESSAGE, path(['response', 'data', 'ValidationFailures', [0], 'errorMessage'], err))
      } else {
        commit(types.CREATE_ERROR_MESSAGE, 'Failed to save forbidden email prefix')
      }
      throw err
    } finally {
      commit(types.SET_SPINNER_STATUS, false)
    }
  },
  async deleteForbiddenDomain ({ commit }, id) {
    try {
      commit(types.SET_SPINNER_STATUS, true)
      await this.$axios.delete(api.generalSettings.deleteDomain(id))
      commit(types.CREATE_SUCCESS_MESSAGE, 'Domain has been deleted')
    } catch (err) {
      commit(types.CREATE_ERROR_MESSAGE, 'Failed to delete forbidden email domain')
      throw err
    } finally {
      commit(types.SET_SPINNER_STATUS, false)
    }
  },

  // Files in database
  async fetchFiles ({ commit }) {
    try {
      commit(types.SET_SPINNER_STATUS, true)
      const { data: { files } } = await this.$axios.get(api.generalSettings.files())
      commit(types.SET_FILES, files)
    } catch (err) {
      commit(types.CREATE_ERROR_MESSAGE, 'FAILED: Loading files')
      throw err
    } finally {
      commit(types.SET_SPINNER_STATUS, false)
    }
  },
  async uploadFileToDatabase ({ commit }, { fileUrl, fileName }) {
    try {
      commit(types.SET_SPINNER_STATUS, true)

      const file = {
        fileUrl,
        fileName,
        categories: ['pdfFile']
      }
      const payload = {
        files: [file]
      }

      const {data: { files }} = await this.$axios.post(api.generalSettings.files(), payload)
      await this.dispatch('fetchFiles')
      if (!isEmpty(files[0])) {
        return files[0].fileId
      }
    } catch (err) {
      commit(types.CREATE_ERROR_MESSAGE, 'FAILED: Uploading file to database')
      throw err
    } finally {
      commit(types.SET_SPINNER_STATUS, false)
    }
  },

  // Files in Cloudinary
  async uploadFileToStorage ({ commit }, file) {
    try {
      commit(types.SET_SPINNER_STATUS, true)

      // upload file to the Cloudinary
      const payload = new FormData()
      payload.append('file', file)

      const {data: { storedFileResponses }} = await this.$axios({
        method: 'post',
        url: api.generalSettings.filesStorage.upload(),
        headers: {'Content-type': 'multipart/form-data'},
        data: payload
      })

      if (!isEmpty(storedFileResponses[0])) {
        const fileId = await this.dispatch('uploadFileToDatabase', {
          fileUrl: storedFileResponses[0].originalFileUrl,
          fileName: storedFileResponses[0].fileName
        })
        return fileId
      } else {
        throw new Error('FAILED: Upload file to Cloudinary')
      }
    } catch (err) {
      commit(types.CREATE_ERROR_MESSAGE, 'FAILED: Uploading file to storage')
      throw err
    } finally {
      commit(types.SET_SPINNER_STATUS, false)
    }
  },

  async putGooglePixelNotification ({commit}, payload) {
    try {
      commit(types.SET_SPINNER_STATUS, true)
      const { data } = await this.$axios.put(api.google.pixel(), payload)
      commit(types.SET_GOOGLE_PIXEL_SETTING, data)
    } catch (err) {
      commit(types.CREATE_ERROR_MESSAGE, 'Error on updating google pixel setting')
    } finally {
      commit(types.SET_SPINNER_STATUS, false)
    }
  },
  async submitStyleSettings ({ commit, state }, payload) {
    try {
      commit(types.SET_SPINNER_STATUS, true)
      /**
       * TODO: remove sync with current GSS before submit
       * during completion of FE-3198
       */
      const settingsUrl = api.generalSettings.getSettings()
      const settingsResponse = await this.$axios.get(settingsUrl)
      const styleSettings = settingsResponse.data.setting.globalStyleSettings
      styleSettings[payload.key] = payload.settings
      const updateStylesUrl = api.generalStyles.updateGeneralStyles()
      await this.$axios.put(updateStylesUrl, styleSettings)
      commit(types.SET_STYLES_SETTINGS, { settings: payload.settings, key: payload.key })
      commit(types.CREATE_SUCCESS_MESSAGE, 'Settings was updated!')
    } catch (err) {
      commit(types.CREATE_ERROR_MESSAGE, 'Error in styles settings')
      throw err
    } finally {
      commit(types.SET_SPINNER_STATUS, false)
    }
  },
  async resetStyleSettings ({ commit }) {
    try {
      commit(types.SET_SPINNER_STATUS, true)
      const stylesSettingsUrl = api.generalStyles.updateGeneralStyles()
      await this.$axios.put(stylesSettingsUrl, defaultStyleSettings)
      commit(types.RESET_STYLES_SETTINGS)
      commit(types.CREATE_SUCCESS_MESSAGE, 'Settings is reset to default state!')
    } catch (err) {
      commit(types.CREATE_ERROR_MESSAGE, 'Error during reset global style settings')
    } finally {
      commit(types.SET_SPINNER_STATUS, false)
    }
  },
  async putAttendeePassword ({ dispatch, commit }, payload) {
    try {
      commit(types.SET_SPINNER_STATUS, true)
      await this.$axios.put(api.generalSettings.putAttendeePassword(), payload)
      commit(types.CREATE_SUCCESS_MESSAGE, 'Attendee password settings was updated successfully')
    } catch (err) {
      commit(types.CREATE_ERROR_MESSAGE, 'Attendee password settings wasn\'t updated')
      throw err
    } finally {
      commit(types.SET_SPINNER_STATUS, false)
    }
  }
}

const mutations = {
  [types.SET_FILES] (state, files) {
    state.files = [...files]
  },
  [types.SET_SETTINGS] (state, { setting }) {
    state.settings = { ...setting }
  },
  [types.SET_GOOGLE_PIXEL_SETTING] (state, { setting }) {
    state.SET_GOOGLE_PIXEL_SETTING = { ...setting }
  },
  [types.SET_FORBIDDEN_EMAIL_DOMAINS] (state, { forbiddenEmailDomains, totalPages, totalItems }) {
    state.forbiddenEmailDomains = forbiddenEmailDomains
    state.domainsTotalPages = totalPages
    state.domainsTotalItems = totalItems
  },
  [types.SET_STYLES_SETTINGS] (state, {key, settings}) {
    state.settings.globalStyleSettings[key] = settings
  },
  [types.RESET_STYLES_SETTINGS] (state) {
    state.settings.globalStyleSettings = defaultStyleSettings
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/general-settings.js